<template>
  <transition name="slide-down">
    <div
      v-if="showIndicator"
      class="fixed top-0 left-0 right-0 z-50 safe-area-top"
    >
      <div
        class="px-4 py-3 text-center text-sm font-medium shadow-lg"
        :class="isOnline 
          ? 'bg-green-600 text-white' 
          : 'bg-yellow-600 text-white'"
      >
        <div class="flex items-center justify-center gap-2">
          <ion-icon
            :icon="isOnline ? wifiOutline : cloudOfflineOutline"
            class="text-lg"
          ></ion-icon>
          <span>{{ message }}</span>
          <ion-badge v-if="queueSize > 0" color="light">
            {{ queueSize }}
          </ion-badge>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { IonIcon, IonBadge } from '@ionic/vue'
import { wifiOutline, cloudOfflineOutline } from 'ionicons/icons'
import { useI18n } from '@/i18n'
import {
  onNetworkStatusChange,
  getNetworkStatus,
  getOfflineQueueSize,
  syncOfflineQueue,
  type NetworkStatus
} from '@/services/offline'

const { t } = useI18n()

const isOnline = ref(true)
const queueSize = ref(0)
const showIndicator = ref(false)
const autoHideTimeout = ref<number | null>(null)

let unsubscribe: (() => void) | null = null

const message = computed(() => {
  if (isOnline.value) {
    return queueSize.value > 0 
      ? t('syncingOfflineChanges') 
      : t('backOnline')
  }
  return t('workingOffline')
})

onMounted(() => {
  // Get initial status
  const { status } = getNetworkStatus()
  isOnline.value = status === 'online'
  updateQueueSize()
  
  // Subscribe to network changes
  unsubscribe = onNetworkStatusChange(handleNetworkChange)
})

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe()
  }
  if (autoHideTimeout.value) {
    clearTimeout(autoHideTimeout.value)
  }
})

function handleNetworkChange(status: NetworkStatus): void {
  const wasOnline = isOnline.value
  isOnline.value = status === 'online'
  
  // Show indicator on status change
  showIndicator.value = true
  
  // Update queue size
  updateQueueSize()
  
  // If we just came back online, sync and hide after delay
  if (!wasOnline && isOnline.value) {
    syncOfflineQueue().then(() => {
      updateQueueSize()
      scheduleAutoHide(3000) // Hide after 3 seconds
    })
  } else if (!isOnline.value) {
    // If we went offline, keep showing until online
    if (autoHideTimeout.value) {
      clearTimeout(autoHideTimeout.value)
      autoHideTimeout.value = null
    }
  }
}

function updateQueueSize(): void {
  queueSize.value = getOfflineQueueSize()
}

function scheduleAutoHide(delay: number): void {
  if (autoHideTimeout.value) {
    clearTimeout(autoHideTimeout.value)
  }
  
  autoHideTimeout.value = window.setTimeout(() => {
    if (queueSize.value === 0) {
      showIndicator.value = false
    }
    autoHideTimeout.value = null
  }, delay)
}
</script>

<style scoped>
.safe-area-top {
  padding-top: env(safe-area-inset-top);
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-down-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-down-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
